import { useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { kitRetryAfterSeconds, verifyKitCode } from '../api/kitAuth'
import { saveKitAccess, toKitAccessClub, type KitAccess } from './kitAccessStore'
import { FALLBACK_LOCKOUT_S, useLockoutCountdown } from './useLockoutCountdown'
import { DEV_TEST_ACCESS, isLocalDev } from './devTestAccess'
import { DEVICE_BINDING_COPY, type KitDeviceKind } from './deviceCopy'

/**
 * PASO 1 de las dos superficies compartidas del club: ligar el DISPOSITIVO al
 * club con el código impreso en el kit. Verifica contra `/auth/kit/verify` y
 * persiste el binding {code, club} en `kitAccessStore` (misma key y mismo
 * shape que el Quest). Recién después viene el paso 2, el PIN del jugador.
 *
 * Presentacional a propósito (no lee el contexto del modo tablet): lo usan el
 * modo tablet (`/tablet`) y la entrada del jugador en `/login` cuando el
 * dispositivo todavía no está ligado a ningún kit. El espejo en el visor es
 * `vr-app/src/ui/KitLogin.tsx::CodeScreen`, con la misma copia.
 *
 * `reason` es el motivo por el que se volvió a pedir el código cuando el
 * dispositivo YA había estado ligado (kit revocado, servicio del club
 * pausado): sin él la pantalla parece un primer arranque y el staff no sabe
 * qué se rompió.
 */
export function KitDeviceScreen({
  onVerified,
  device = 'device',
  title,
  description,
  reason,
  className = 'panel max-w-xl p-2xl',
  footer,
}: {
  onVerified: (access: KitAccess) => void
  device?: KitDeviceKind
  title?: string
  description?: string
  reason?: string | null
  className?: string
  footer?: React.ReactNode
}) {
  const copy = DEVICE_BINDING_COPY[device]
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { lockedFor, locked, startLockout } = useLockoutCountdown()

  const canSubmit = value.trim().length > 0 && !busy && !locked

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    await verify(value.trim().toUpperCase())
  }

  async function verify(code: string) {
    if (busy || locked) return
    setBusy(true)
    setError(null)
    try {
      const res = await verifyKitCode(code)
      onVerified(saveKitAccess(code, toKitAccessClub(res.club)))
    } catch (err) {
      // 429: el backend throttlea por IP igual que el login por PIN. No se
      // toca el kitAccess guardado — el throttling no invalida un binding.
      const retry = kitRetryAfterSeconds(err)
      if (retry !== null || (err instanceof ApiError && err.status === 429)) {
        startLockout(retry ?? FALLBACK_LOCKOUT_S)
        setError(null)
      } else {
        setError(
          err instanceof ApiError && err.status === 404
            ? 'Código inválido. Revisá el código de club e intentá de nuevo.'
            : err instanceof Error
              ? err.message
              : 'Error de red.',
        )
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={className}>
      <p className="m-0 text-xs uppercase tracking-wide text-text-muted">{copy.chrome}</p>
      <h1 className="mt-xs text-2xl">{title ?? copy.title}</h1>
      <p className="mt-sm text-base text-text-secondary">{description ?? copy.description}</p>

      <form onSubmit={onSubmit} className="mt-lg flex flex-col gap-md">
        {reason && <div className="banner-err">{reason}</div>}
        {locked ? (
          <div className="banner-err">Demasiados intentos. Probá de nuevo en {lockedFor} s.</div>
        ) : (
          error && <div className="banner-err">{error}</div>
        )}
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          placeholder="CÓDIGO"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          disabled={busy || locked}
          autoFocus
          className="input py-lg text-center text-2xl tracking-[0.3em] disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className="btn-primary py-lg text-lg disabled:opacity-50"
        >
          {busy ? 'Verificando…' : 'Continuar'}
        </button>
      </form>
      {footer}
      {isLocalDev() && (
        <DevTestAccessHint
          actionLabel={`Usar código de prueba (${DEV_TEST_ACCESS.kitCode})`}
          onAction={() => void verify(DEV_TEST_ACCESS.kitCode)}
          disabled={busy || locked}
        />
      )}
    </div>
  )
}

/**
 * Chuleta de acceso de prueba, SOLO en localhost / dev: club, código del kit
 * de fantasía y jugador de prueba con su PIN (los que el backend asegura al
 * arrancar), con un botón que los aplica. Pedido de mg (2026-09-07). Mismo
 * bloque en el vr-app (`ui/KitLogin.tsx`).
 */
export function DevTestAccessHint({
  actionLabel,
  onAction,
  disabled = false,
}: {
  actionLabel: string
  onAction: () => void
  disabled?: boolean
}) {
  const t = DEV_TEST_ACCESS
  return (
    <div className="card mt-lg text-xs text-text-secondary">
      <span className="tag tag-warn">Dev · solo localhost</span>
      <p className="mt-sm">
        <b>{t.clubName}</b> · código de club: <b>{t.kitCode}</b>
      </p>
      <p className="mt-xs">
        {t.playerDisplayName}: PIN <b>{t.playerPin}</b> · usuario/contraseña <b>{t.playerUsername}</b> / <b>{t.playerPassword}</b>
      </p>
      <button type="button" className="btn-ghost mt-sm px-md py-sm" onClick={onAction} disabled={disabled}>
        {actionLabel}
      </button>
    </div>
  )
}
